import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, Lock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * SecureAccessWrapper
 * 
 * HOC that wraps protected pages and requires passcode verification.
 * Handles the full flow: confirmation → code request → verification → session management
 * 
 * Session persists for the browser session (until tab/browser is closed)
 * No inactivity timer - session is tied to browser session storage
 * 
 * @param {React.Component} children - The protected page component
 * @param {string} pageName - Name of the protected page (for display)
 * @param {string} pageKey - Key for the page (e.g., "financial-dashboard")
 * @param {function} onSave - Callback when save operation completes (to end session)
 */
const SecureAccessWrapper = ({ children, pageName, pageKey, onSave }) => {
  const navigate = useNavigate();
  
  // State
  const [step, setStep] = useState('confirm'); // 'confirm' | 'requesting' | 'enter-code' | 'verifying' | 'authenticated'
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  
  // Refs
  const inputRefs = useRef([]);
  const digitsRef = useRef(['', '', '', '', '', '']);
  const verifyingRef = useRef(false);

  // Listen for sidebar state changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    
    // Listen for storage events (from other tabs) and custom events (same tab)
    window.addEventListener('storage', handleStorageChange);
    
    // Poll for changes (since storage event doesn't fire in same tab)
    const interval = setInterval(handleStorageChange, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const existingSession = sessionStorage.getItem(`secure-session-${pageKey}`);
    if (existingSession) {
      // Session exists in sessionStorage, user is authenticated for this browser session
      setSessionId(existingSession);
      setStep('authenticated');
    }
  }, [pageKey]);

  const requestCode = async () => {
    setLoading(true);
    setError('');
    setStep('requesting');
    verifyingRef.current = false;
    digitsRef.current = ['', '', '', '', '', ''];
    setPasscode('');
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/secure-access/request-code`,
        { protected_page: pageKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Access code sent! Check the designated email.');
      setStep('enter-code');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send code';
      setError(errorMessage);
      toast.error(errorMessage);
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (codeToVerify = null) => {
    const code = codeToVerify || passcode;
    
    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    setStep('verifying');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/secure-access/verify-code`,
        { passcode: code, protected_page: pageKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      
      // Store in sessionStorage - persists until browser/tab is closed
      sessionStorage.setItem(`secure-session-${pageKey}`, newSessionId);
      
      setStep('authenticated');
      toast.success('Access granted!');
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Invalid code';
      setError(errorMessage);
      setStep('enter-code');
      verifyingRef.current = false;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    // Clear session and go back to dashboard
    sessionStorage.removeItem(`secure-session-${pageKey}`);
    setSessionId(null);
    setStep('confirm');
    toast.info('Secure session ended');
    navigate('/dashboard');
  };

  const handleSaveComplete = async () => {
    // Mark save as complete
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/secure-access/mark-save-complete`,
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Changes saved successfully!');
      
    } catch (error) {
      console.error('Failed to mark save complete:', error);
    }
    
    // Call the original onSave callback if provided
    if (onSave) {
      onSave();
    }
  };

  // Handle passcode input change for individual boxes
  const handlePasscodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const digit = value.slice(-1);
    digitsRef.current[index] = digit;
    // Build a 6-char string preserving positions (spaces for empty slots)
    const display = digitsRef.current.map(d => d || ' ').join('');
    setPasscode(display);
    
    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits are filled by typing
    const clean = digitsRef.current.join('');
    if (digit && clean.length === 6 && !verifyingRef.current) {
      verifyingRef.current = true;
      setTimeout(() => {
        verifyCode(clean);
      }, 150);
    }
  };

  // Handle paste event - allows pasting full 6-digit code
  const handlePaste = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent double-firing from bubbling
    const pastedData = e.clipboardData.getData('text');
    
    // Extract only digits from pasted content
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      // Fill the digits array
      for (let i = 0; i < 6; i++) {
        digitsRef.current[i] = digits[i] || '';
      }
      const display = digitsRef.current.map(d => d || ' ').join('');
      setPasscode(display);
      
      // Focus the appropriate input based on paste length
      const focusIndex = Math.min(digits.length, 5);
      inputRefs.current[focusIndex]?.focus();
      
      // If we got all 6 digits, auto-submit with the pasted digits directly
      if (digits.length === 6 && !verifyingRef.current) {
        verifyingRef.current = true;
        setTimeout(() => {
          verifyCode(digits);
        }, 150);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (digitsRef.current[index]) {
        // Clear current box
        digitsRef.current[index] = '';
        setPasscode(digitsRef.current.map(d => d || ' ').join(''));
      } else if (index > 0) {
        // Move to previous box and clear it
        digitsRef.current[index - 1] = '';
        setPasscode(digitsRef.current.map(d => d || ' ').join(''));
        inputRefs.current[index - 1]?.focus();
      }
      verifyingRef.current = false;
      e.preventDefault();
    }
    // Handle Enter
    const clean = digitsRef.current.join('');
    if (e.key === 'Enter' && clean.length === 6 && !verifyingRef.current) {
      verifyingRef.current = true;
      verifyCode(clean);
    }
  };

  // Render confirmation dialog (step 1)
  if (step === 'confirm' || step === 'requesting') {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              Secure Access Required
            </DialogTitle>
            <DialogDescription>
              <strong>{pageName}</strong> contains sensitive information and requires additional verification.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert className="bg-amber-50 border-amber-200">
              <Lock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                A 6-digit verification code will be sent to the designated admin email for verification.
              </AlertDescription>
            </Alert>
            
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={requestCode} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Code...
                  </>
                ) : (
                  'Request Access Code'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Render code entry dialog (step 2)
  if (step === 'enter-code' || step === 'verifying') {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Enter Verification Code
            </DialogTitle>
            <DialogDescription>
              Enter the 6-digit code sent to the admin email. You can paste the code directly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* 6-digit code input with paste support */}
            <div className="flex justify-center gap-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="w-12 h-12 text-center text-2xl font-mono"
                  value={passcode[index]?.trim() || ''}
                  onChange={(e) => handlePasscodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <p className="text-sm text-muted-foreground text-center">
              Code expires in 10 minutes. You can copy and paste the full code.
            </p>
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button variant="link" onClick={requestCode} disabled={loading}>
                Resend Code
              </Button>
              <Button onClick={() => { const c = digitsRef.current.join(''); if (c.length === 6) { verifyingRef.current = true; verifyCode(c); } }} disabled={loading || passcode.replace(/\s/g, '').length !== 6}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Access'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Render authenticated state (show the protected content)
  if (step === 'authenticated') {
    return (
      <div>
        {/* Secure session indicator bar - positioned to not overlap sidebar */}
        <div className={`fixed top-0 right-0 z-30 bg-primary transition-all duration-300 left-0 ${
          sidebarCollapsed ? 'lg:left-14' : 'lg:left-52'
        }`}>
          <div className="px-4 py-2 flex items-center justify-between text-white text-sm">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>Secure Access Mode - {pageName}</span>
              <CheckCircle className="h-4 w-4 text-green-300" />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white/80 text-xs hidden sm:inline">Session active until browser closes</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:text-white hover:bg-white/20 h-7 px-3"
              >
                End Session
              </Button>
            </div>
          </div>
        </div>
        
        {/* Main content with padding for the indicator bar */}
        <div className="pt-10">
          {React.cloneElement(children, { onSecureSave: handleSaveComplete })}
        </div>
      </div>
    );
  }

  return null;
};

export default SecureAccessWrapper;
